import { log } from "@/Utils/Debug";

interface ResolveConfigInterface {
  basePromise?: unknown;
  debug?: boolean;
}

export interface Deferred<T = unknown, E = unknown> {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason: E) => void;
  promise: Promise<T>;
}

export const waitFor = async (seconds: number, isReject = false): Promise<void> =>
  new Promise((res, reject) => {
    setTimeout(() => {
      if (isReject) reject(new Error("Promise timeout"));
      else res();
    }, seconds * 1000);
  });

export const not = (promise: Promise<boolean>) => promise.then((res: boolean) => !res);

export const returnUndefined: () => undefined = () => undefined;

export const resolveLineal = async <T>(
  listInputs: unknown[],
  promiseFunction: (a: unknown) => Promise<T>,
  { basePromise = false, debug = false }: ResolveConfigInterface = {}
): Promise<T[]> => {
  const results = [];
  let index = 1;

  results.push(
    await listInputs.reduce(
      async (p, input) => {
        const result = await p;

        if (debug) {
          log(`Executed ${index++} of ${listInputs.length}`);
        }

        results.push(result);

        try {
          return promiseFunction(input);
        } catch (e) {
          return { error: e };
        }
      },
      basePromise ? basePromise : Promise.resolve(0)
    )
  );
  results.shift();

  return results as T[];
};

export const withTimeout = <T = unknown>(
  call: () => Promise<T>,
  seconds: number,
  isReject = true
): Promise<T> => {
  return Promise.race([waitFor(seconds, isReject), call()]) as Promise<T>;
};

export function defer<T = void, E = unknown>(): Deferred<T, E> {
  const promise: Deferred<T, E> = {} as Deferred<T, E>;

  promise.promise = new Promise<T>((_resolve, _reject) => {
    promise.resolve = _resolve;
    promise.reject = _reject;
  });

  return promise;
}
